import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { type RootState } from '@/store';
import Cookies from 'js-cookie';
import {
  decryptPayload,
  encryptPayload,
  isEncryptedResponse,
  isEncryptionEnabled,
  isJsonBody,
} from '@/utils/payloadCrypto';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.accessToken;
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const encryptedBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  if (!isEncryptionEnabled) return baseQuery(args, api, extraOptions);

  let finalArgs = args;
  if (typeof args === 'object' && isJsonBody(args.body)) {
    finalArgs = { ...args, body: { payload: await encryptPayload(args.body) } };
  }

  const result = await baseQuery(finalArgs, api, extraOptions);
  // Errors (4xx/5xx) come back as plain JSON, only successful bodies are encrypted
  if (!result.error && isEncryptedResponse(result.data)) {
    try {
      return { ...result, data: await decryptPayload(result.data.payload) };
    } catch {
      return {
        error: { status: 'CUSTOM_ERROR', error: 'Failed to decrypt response' },
        meta: result.meta,
      };
    }
  }
  return result;
};

const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  const result = await encryptedBaseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    console.warn('Unauthorized! Logging out user...');
    Cookies.remove('admin_authToken');
    setTimeout(() => {
      if (window.location.pathname === '/login') return;
      window.location.href = '/login';
    }, 2000);
  }
  return result;
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: true,
  keepUnusedDataFor: 0,
  tagTypes: [
    'User',
    'MfaStatus',
    'Auth',
    'FeatureFlags',
    'Common',
    'Bankers',
    'Account',
    'Crypto',
    'GetNotification',
    'Members',
    'GetBanner',
    'Casino',
    'Getsupport',
    'Event',
    'Market',
    'BetConfig',
    'Wallet',
    'Provider',
    'Bonus',
    'BalanceSummary',
    'UserManagement',
    'GameAnalytics',
    'CasinoAnalytics',
    'BusinessAnalytics',
    'TopUsers',
    'TopCategories',
    'DeviceBreakdown',
    'BonusAnalytics',
    'LoginSummary',
    'LiveGames',
    'BalanceSummaryDataApi',
    'accessMember',
    'UserSummary',
    'Competitions',
    'Group',
    'Catalogue',
    'Popular',
    'Sports',
    'PinnedMarkets',
    'Settlement',
  ],
  endpoints: () => ({}),
});

// Export hooks for usage in functional components
export const {
  util: { getRunningQueriesThunk },
} = apiSlice;

// Export the api slice for use in store
export default apiSlice;
